﻿/**
 * 增强版预算对比组件
 * 支持预算成分对比分析、应急储备、预算锁定
 */
import React, { useState } from 'react';
import { Typography, Progress, Card, Space, Tag, Row, Col, Tooltip, Button, Modal, Input, message } from 'antd';
import {
  UserOutlined,
  ToolOutlined,
  GoldOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  LockOutlined,
  UnlockOutlined,
  HistoryOutlined,
  EditOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import type { BudgetBreakdown, RevenueBreakdown } from '../../types';
import { calculateBudgetHealth, formatMoney, getBudgetTagColor, BudgetStatus } from '../../utils/budgetUtils';
import { lockBudget, unlockBudget } from '../../services/budgetService';
import BudgetHistoryModal from '../BudgetMonitor/BudgetHistoryModal';
import type { CostDetailType } from './CostDetailTypes';
import { useAuthStore } from '../../store/authStore';
import { hasSensitivePermission } from '../../utils/sensitivePermissions';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface BudgetComparisonEnhancedProps {
  project: ProjectOverviewData & { 
    budgetBreakdown?: BudgetBreakdown;
    revenueTotal?: number;
    revenueBreakdown?: RevenueBreakdown;
    budgetVersion?: number;
    budgetLocked?: boolean;
    budgetLockedAt?: string;
    budgetLockedBy?: { username: string; realName?: string };
  };
  stats?: any;
  isMobile?: boolean;
  onBudgetLockChange?: () => void;
  onEditProject?: () => void;
  onViewDetail?: (type: CostDetailType) => void;
  onAddRecord?: (type: CostDetailType) => void;
}

// 预算成分配置
const BUDGET_CATEGORIES = [
  {
    key: 'labor',
    label: '人工',
    icon: <UserOutlined />,
    color: '#1677ff',
    costKey: 'workTimeCost'
  },
  {
    key: 'equipment',
    label: '机械',
    icon: <ToolOutlined />,
    color: '#52c41a',
    costKey: 'equipmentCost'
  },
  {
    key: 'material',
    label: '材料',
    icon: <GoldOutlined />,
    color: '#fa8c16',
    costKey: 'materialCost'
  },
  {
    key: 'coordination',
    label: '协调',
    icon: <FileTextOutlined />,
    color: '#722ed1',
    costKey: 'coordinationCost'
  },
  {
    key: 'contingency',
    label: '应急',
    icon: <SafetyCertificateOutlined />,
    color: '#eb2f96',
    costKey: null // 应急储备不对应实际成本
  }
];

const COST_ITEMS: Array<{
  key: CostDetailType;
  label: string;
  icon: React.ReactNode;
  color: string;
  costKey: 'workTimeCost' | 'equipmentCost' | 'materialCost' | 'subcontractCost' | 'coordinationCost';
  percentageKey: 'workTimePercentage' | 'equipmentPercentage' | 'materialPercentage' | 'subcontractPercentage' | 'coordinationPercentage';
}> = [
  {
    key: 'worktime',
    label: '人工成本',
    icon: <UserOutlined />,
    color: '#547590',
    costKey: 'workTimeCost',
    percentageKey: 'workTimePercentage',
  },
  {
    key: 'equipment',
    label: '机械成本',
    icon: <ToolOutlined />,
    color: '#5ad1b2',
    costKey: 'equipmentCost',
    percentageKey: 'equipmentPercentage',
  },
  {
    key: 'material',
    label: '材料成本',
    icon: <GoldOutlined />,
    color: '#b99258',
    costKey: 'materialCost',
    percentageKey: 'materialPercentage',
  },
  {
    key: 'subcontract',
    label: '分包成本',
    icon: <FileTextOutlined />,
    color: '#9384ff',
    costKey: 'subcontractCost',
    percentageKey: 'subcontractPercentage',
  },
  {
    key: 'coordination',
    label: '协调成本',
    icon: <SafetyCertificateOutlined />,
    color: '#6ee7d8',
    costKey: 'coordinationCost',
    percentageKey: 'coordinationPercentage',
  }
];

// 获取状态图标
const getStatusIcon = (status: BudgetStatus) => {
  switch (status) {
    case 'healthy':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'good':
      return <CheckCircleOutlined style={{ color: '#1677ff' }} />;
    case 'warning':
      return <WarningOutlined style={{ color: '#fa8c16' }} />;
    case 'danger':
      return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
    case 'over':
      return <CloseCircleOutlined style={{ color: '#cf1322' }} />;
    default:
      return <MinusCircleOutlined style={{ color: '#999' }} />;
  }
};

const BudgetComparisonEnhanced: React.FC<BudgetComparisonEnhancedProps> = ({
  project,
  stats,
  isMobile = false,
  onBudgetLockChange,
  onEditProject,
  onViewDetail,
  onAddRecord,
}) => {
  const { checkPermission } = useAuthStore();
  const canViewBudget = hasSensitivePermission(checkPermission, 'budget');
  const canViewContract = hasSensitivePermission(checkPermission, 'contract');
  const canViewProfit = hasSensitivePermission(checkPermission, 'profit');
  const canViewChangeOrder = hasSensitivePermission(checkPermission, 'changeorder');
  const canViewSettlement = hasSensitivePermission(checkPermission, 'settlement');
  const canViewSubcontract = hasSensitivePermission(checkPermission, 'subcontract');
  const [unlockModalVisible, setUnlockModalVisible] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [lockLoading, setLockLoading] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);

  const budgetHealth = calculateBudgetHealth(project.budget, project.actualCost);
  const budgetBreakdown = project.budgetBreakdown;
  const revenueBreakdown = project.revenueBreakdown;
  const totalRevenue = project.revenueTotal || 0;
  const estimatedProfit = totalRevenue - project.actualCost;
  const costBreakdown = stats?.summary?.costBreakdown || {};
  const visibleRevenueTags = [
    canViewContract ? { key: 'contract', label: '合同', value: revenueBreakdown?.contract } : null,
    canViewChangeOrder ? { key: 'changeOrder', label: '增项', value: revenueBreakdown?.changeOrder } : null,
    canViewChangeOrder ? { key: 'claim', label: '签证', value: revenueBreakdown?.claim } : null,
    canViewSettlement ? { key: 'settlement', label: '结算', value: revenueBreakdown?.settlement } : null,
    (canViewContract || canViewChangeOrder || canViewSettlement) ? { key: 'other', label: '其他', value: revenueBreakdown?.other } : null,
  ].filter(Boolean) as Array<{ key: string; label: string; value?: number }>;

  // 始终展示预算成分执行情况模块
  const hasBudgetBreakdown = true;

  // 计算各成分的预算健康度
  const getCategoryHealth = (budgetKey: string, costKey: string | null) => {
    const budget = budgetBreakdown?.[budgetKey as keyof BudgetBreakdown] || 0;
    const cost = costKey ? (costBreakdown[costKey] || 0) : 0;
    return calculateBudgetHealth(budget, cost);
  };

  // 锁定预算
  const handleLockBudget = async () => {
    setLockLoading(true);
    try {
      const response = await lockBudget(project.id);
      if (response.success) {
        message.success('预算已锁定');
        onBudgetLockChange?.();
      } else {
        message.error(response.message || '锁定失败');
      }
    } catch (error) {
      message.error('锁定预算失败');
    } finally {
      setLockLoading(false);
    }
  };

  // 解锁预算
  const handleUnlockBudget = async () => {
    if (!unlockReason.trim()) {
      message.warning('请输入解锁原因');
      return;
    }
    
    setLockLoading(true);
    try {
      const response = await unlockBudget(project.id, unlockReason);
      if (response.success) {
        message.success('预算已解锁');
        setUnlockModalVisible(false);
        setUnlockReason('');
        onBudgetLockChange?.();
      } else {
        message.error(response.message || '解锁失败');
      }
    } catch (error) {
      message.error('解锁预算失败');
    } finally {
      setLockLoading(false);
    }
  };

  return (
    <div
      style={{
        marginBottom: isMobile ? 16 : 24,
        padding: isMobile ? 14 : 18,
        borderRadius: 20,
        background: 'rgba(210, 218, 224, 0.5)',
        border: '1px solid rgba(16, 27, 35, 0.08)'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <Title level={5} style={{ margin: 0, fontSize: isMobile ? 14 : 16, color: '#10171d' }}>
          预算对比分析
          {project.budgetLocked && (
            <Tag icon={<LockOutlined />} color="red" style={{ marginLeft: 8 }}>
              已锁定
            </Tag>
          )}
        </Title>
        <Space size={8} wrap>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={onEditProject}
            style={{ borderColor: '#547590', color: '#2f4d66', fontWeight: 600, boxShadow: '0 2px 8px rgba(84, 117, 144, 0.18)' }}
          >
            {isMobile ? '' : '调整预算'}
          </Button>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => setHistoryModalVisible(true)}
          >
            {isMobile ? '' : '调整历史'}
          </Button>
          {project.budgetLocked ? (
            <Button
              size="small"
              icon={<UnlockOutlined />}
              onClick={() => setUnlockModalVisible(true)}
              loading={lockLoading}
            >
              {isMobile ? '' : '解锁'}
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              icon={<LockOutlined />}
              onClick={handleLockBudget}
              loading={lockLoading}
            >
              {isMobile ? '' : '锁定'}
            </Button>
          )}
        </Space>
      </div>
      
      {/* 总体预算对比 */}
      <Card
        size="small"
        style={{
          backgroundColor: '#fafafa',
          borderRadius: 16,
          marginBottom: 16,
          border: '1px solid rgba(16, 27, 35, 0.08)',
          boxShadow: 'none'
        }}
        bodyStyle={{ padding: isMobile ? 12 : 16 }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: 12,
          marginBottom: 12
        }}>
          <div style={{
            padding: isMobile ? '10px 12px' : '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(16, 27, 35, 0.09)',
            background: '#ffffff',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)'
          }}>
            <Text type="secondary" style={{ fontSize: 12 }}>预算总额</Text>
            <div style={{ marginTop: 4 }}>
              <Text strong style={{ fontSize: isMobile ? 16 : 18 }}>
                {canViewBudget ? formatMoney(project.budget, false) : '--'}
              </Text>
            </div>
          </div>
          <div style={{
            padding: isMobile ? '10px 12px' : '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(16, 27, 35, 0.09)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.75)'
          }}>
            <Text type="secondary" style={{ fontSize: 12 }}>累计成本</Text>
            <div style={{ marginTop: 4 }}>
              <Text strong style={{ fontSize: isMobile ? 16 : 18, color: budgetHealth.color }}>
                {formatMoney(project.actualCost, false)}
              </Text>
            </div>
          </div>
          <div style={{
            padding: isMobile ? '10px 12px' : '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(22, 119, 255, 0.12)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f3f8ff 100%)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.75)'
          }}>
            <Text type="secondary" style={{ fontSize: 12 }}>累计收入</Text>
            <div style={{ marginTop: 4 }}>
              <Text strong style={{ fontSize: isMobile ? 16 : 18, color: totalRevenue > 0 ? '#1677ff' : '#8c8c8c' }}>
                {canViewContract ? formatMoney(totalRevenue, false) : '--'}
              </Text>
            </div>
          </div>
          <div style={{
            padding: isMobile ? '10px 12px' : '12px 14px',
            borderRadius: 14,
            border: estimatedProfit >= 0 ? '1px solid rgba(82, 196, 26, 0.16)' : '1px solid rgba(245, 34, 45, 0.14)',
            background: estimatedProfit >= 0 ? 'linear-gradient(180deg, #ffffff 0%, #f6fff6 100%)' : 'linear-gradient(180deg, #ffffff 0%, #fff7f7 100%)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.75)'
          }}>
            <Text type="secondary" style={{ fontSize: 12 }}>预估利润</Text>
            <div style={{ marginTop: 4 }}>
              <Text strong style={{ fontSize: isMobile ? 16 : 18, color: estimatedProfit >= 0 ? '#52c41a' : '#f5222d' }}>
                {canViewProfit ? `${estimatedProfit >= 0 ? '' : '-'}${formatMoney(Math.abs(estimatedProfit), false)}` : '--'}
              </Text>
            </div>
          </div>
        </div>

        {/* 进度条 */}
        <Progress
          percent={Math.min(budgetHealth.percentage, 100)}
          strokeColor={budgetHealth.color}
          railColor="#e8e8e8"
          format={() => `${budgetHealth.percentage.toFixed(1)}%`}
          style={{ marginBottom: 12 }}
        />

        {/* 状态提示 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: isMobile ? 12 : 14 }}>整体状态:</Text>
          <Tag color={getBudgetTagColor(budgetHealth.status)}>
            {budgetHealth.icon} {budgetHealth.label}
          </Tag>
        </div>
        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12, display: 'block', marginTop: 4 }}>
          {budgetHealth.message}
        </Text>

        {visibleRevenueTags.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e8e8e8' }}>
            <Space size={[8, 8]} wrap>
              {visibleRevenueTags.map(item => (
                <Tag key={item.key} color="geekblue" style={{ borderRadius: 999 }}>
                  {item.label} {formatMoney(item.value || 0, false)}
                </Tag>
              ))}
            </Space>
          </div>
        )}
      </Card>

      {/* 预算成分对比（如果有配置） */}
      {hasBudgetBreakdown && (
        <Card
          size="small"
          title={
            <Space>
              <span style={{ fontSize: isMobile ? 13 : 14, color: '#10171d' }}>📊 预算成分执行情况</span>
            </Space>
          }
          style={{
            borderRadius: 16,
            border: '1px solid rgba(16, 27, 35, 0.08)',
            boxShadow: 'none'
          }}
          bodyStyle={{ padding: isMobile ? 8 : 12 }}
        >
          <Row gutter={[8, 8]}>
            {BUDGET_CATEGORIES.map((category) => {
              const budget = budgetBreakdown?.[category.key as keyof BudgetBreakdown] || 0;
              const cost = category.costKey ? (costBreakdown[category.costKey] || 0) : 0;
              const health = getCategoryHealth(category.key, category.costKey);
              
              // 应急储备特殊处理
              const isContingency = category.key === 'contingency';
              const canShowCategory = category.key !== 'subcontract' || canViewSubcontract;

              return (
                <Col xs={12} sm={12} md={6} key={category.key}>
                  <Card
                    size="small"
                    style={{ 
                      backgroundColor: isContingency ? '#fff0f6' : '#fafafa',
                      borderColor: isContingency ? '#ffadd2' : 'rgba(16, 27, 35, 0.08)',
                      borderRadius: 14,
                      boxShadow: 'none',
                      height: '100%'
                    }}
                    bodyStyle={{ padding: isMobile ? 8 : 12 }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <Space size={4}>
                        <span style={{ color: category.color }}>{category.icon}</span>
                        <Text strong style={{ fontSize: isMobile ? 12 : 13 }}>
                          {category.label}
                        </Text>
                        {!isContingency && (
                          <Tooltip title={health.message}>
                            {getStatusIcon(health.status)}
                          </Tooltip>
                        )}
                        {isContingency && (
                          <Tag color="pink" style={{ fontSize: 10 }}>储备</Tag>
                        )}
                      </Space>
                    </div>
                    
                    <div style={{ marginBottom: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {isContingency ? '储备: ' : '预算: '}
                      </Text>
                      <Text style={{ fontSize: 12 }}>{canShowCategory && canViewBudget ? formatMoney(budget, false) : '--'}</Text>
                    </div>
                    
                    {!isContingency && (
                      <>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>已用: </Text>
                          <Text style={{ fontSize: 12, color: health.color }}>
                            {canShowCategory ? formatMoney(cost, false) : '--'}
                          </Text>
                        </div>
                        
                        <Progress
                          percent={canShowCategory ? Math.min(health.percentage, 100) : 0}
                          strokeColor={health.color}
                          size="small"
                          format={() => canShowCategory ? `${health.percentage.toFixed(0)}%` : '--'}
                        />
                      </>
                    )}
                    
                    {isContingency && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          状态: <Tag color="green" style={{ fontSize: 10 }}>未动用</Tag>
                        </Text>
                      </div>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      )}

      {/* 没有预算成分配置时的提示 */}
      {!hasBudgetBreakdown && project.budget > 0 && (
        <Card
          size="small"
          style={{
            backgroundColor: '#fffbe6',
            borderColor: '#ffe58f',
            borderRadius: 16,
            boxShadow: 'none'
          }}
          bodyStyle={{ padding: isMobile ? 12 : 16 }}
        >
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
            💡 提示：您可以在项目设置中配置预算成分（人工、机械、材料、协调、应急），以获得更精细的成本分析
          </Text>
        </Card>
      )}

      {/* 成本构成 */}
      <Card
        size="small"
        title={<span style={{ fontSize: isMobile ? 13 : 14, color: '#10171d' }}>成本构成</span>}
        style={{
          borderRadius: 16,
          border: '1px solid rgba(16, 27, 35, 0.08)',
          boxShadow: 'none',
          marginTop: 16,
        }}
        bodyStyle={{ padding: isMobile ? 12 : 16 }}
      >
        {COST_ITEMS.map((item, index) => {
          const cost = costBreakdown[item.costKey] || 0;
          const percent = stats?.summary?.costPercentage?.[item.percentageKey] || 0;
          const canShowItem = item.key !== 'subcontract' || canViewSubcontract;

          return (
            <div key={item.key} style={{ marginBottom: index === COST_ITEMS.length - 1 ? 0 : 14 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 8,
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: isMobile ? '100%' : 220 }}>
                  <Space size={8}>
                    <span style={{ color: item.color }}>{item.icon}</span>
                    <Text style={{ fontSize: isMobile ? 13 : 14, color: '#10171d' }}>{item.label}</Text>
                  </Space>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Space size={8}>
                    <Text strong style={{ color: item.color, fontSize: isMobile ? 13 : 14 }}>
                      {canShowItem ? formatMoney(cost, false) : '--'}
                    </Text>
                    <Text style={{ fontSize: isMobile ? 11 : 12, color: '#667986' }}>
                      {canShowItem ? `(${percent.toFixed(1)}%)` : '(--)'}
                    </Text>
                  </Space>

                  <Space size={4} wrap>
                    <Button size="small" type="link" onClick={() => onViewDetail?.(item.key)} style={{ paddingInline: 4, color: '#b99258' }}>
                      查看明细
                    </Button>
                    <Button size="small" type="link" onClick={() => onAddRecord?.(item.key)} style={{ paddingInline: 4, color: '#547590' }}>
                      补录
                    </Button>
                  </Space>
                </div>
              </div>
              <Progress percent={canShowItem ? percent : 0} strokeColor={item.color} railColor="rgba(16, 27, 35, 0.08)" showInfo={false} size="small" />
            </div>
          );
        })}
      </Card>

      {/* 解锁预算弹窗 */}
      <Modal
        title="解锁预算"
        open={unlockModalVisible}
        onOk={handleUnlockBudget}
        onCancel={() => {
          setUnlockModalVisible(false);
          setUnlockReason('');
        }}
        confirmLoading={lockLoading}
        okText="确认解锁"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            解锁后可以修改预算，请输入解锁原因：
          </Text>
        </div>
        <TextArea
          value={unlockReason}
          onChange={(e) => setUnlockReason(e.target.value)}
          placeholder="请输入解锁原因..."
          rows={3}
          maxLength={200}
          showCount
        />
      </Modal>

      {/* 预算调整历史弹窗 */}
      <BudgetHistoryModal
        visible={historyModalVisible}
        projectId={project.id}
        projectName={project.name}
        onClose={() => setHistoryModalVisible(false)}
      />
    </div>
  );
};

export default BudgetComparisonEnhanced;
